import React, { useCallback, useEffect, useRef, useState } from "react";
import { Chevron, Teams } from "./icons";

// Custom listbox so the open menu matches the Figma dropdown (white card, soft
// shadow, rounded rows) rather than a native select popup. Keyboard support is
// hand-rolled to the listbox pattern: Up/Down move, Enter/Space select, Escape
// closes and returns focus, and a click outside dismisses.
const TeamFilter = ({ options, value, onChange, labelId }) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(() => Math.max(0, options.indexOf(value)));
    const rootRef = useRef(null);
    const triggerRef = useRef(null);

    const close = useCallback((returnFocus) => {
        setOpen(false);
        if (returnFocus && triggerRef.current) triggerRef.current.focus();
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onDocPointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("pointerdown", onDocPointerDown);
        return () => document.removeEventListener("pointerdown", onDocPointerDown);
    }, [open]);

    const choose = (option) => {
        onChange(option);
        close(true);
    };

    const onKeyDown = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setActiveIndex(Math.max(0, options.indexOf(value)));
            setOpen(true);
            return;
        }
        if (!open) return;

        if (e.key === "Escape") {
            e.preventDefault();
            close(true);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % options.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + options.length) % options.length);
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            choose(options[activeIndex]);
        } else if (e.key === "Home") {
            e.preventDefault();
            setActiveIndex(0);
        } else if (e.key === "End") {
            e.preventDefault();
            setActiveIndex(options.length - 1);
        }
    };

    return (
        <div ref={rootRef} className="relative md:w-[250px]">
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-labelledby={labelId}
                onClick={() => setOpen((o) => !o)}
                onKeyDown={onKeyDown}
                className="w-full h-12 flex items-center gap-2.5 px-4 bg-white border border-solid border-FGNeutralAccent rounded-lg text-left"
            >
                <Teams aria-hidden="true" focusable="false" />
                <span className="flex-1 min-w-0 truncate text-sm text-grey45">{value}</span>
                <Chevron
                    aria-hidden="true"
                    focusable="false"
                    className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <ul
                    role="listbox"
                    aria-labelledby={labelId}
                    tabIndex={-1}
                    onKeyDown={onKeyDown}
                    className="absolute z-20 left-0 right-0 mt-2 p-2 bg-white border border-solid border-FGNeutralAccent rounded-xl shadow-[0px_12px_36px_0px_rgba(15,20,36,0.12)] list-none"
                >
                    {options.map((option, index) => {
                        const isSelected = option === value;
                        return (
                            <li
                                key={option}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => choose(option)}
                                onMouseEnter={() => setActiveIndex(index)}
                                className={`px-3 py-2 rounded-lg text-sm cursor-pointer text-left ${
                                    index === activeIndex ? "bg-grey30" : ""
                                } ${isSelected ? "font-semibold text-primaryBrand" : "text-grey45"}`}
                            >
                                {option}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default TeamFilter;
